import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import { USERS_API_URL } from '../../config/apiConfig';
import { WikiBadge, IndustryVIPBadge } from '../ContributorsScreen/ContributorsScreen';
import { DiscussionBadge } from '../ContributorsScreen/ContributorsScreen';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import UploadOverlay from '../UploadOverlay/UploadOverlay';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import { linkifyHtml } from '../../utils/linkify';

// Character limit constants
const CHAR_SOFT_LIMIT  = 10000;
const CHAR_WARNING     = 9000;
const CHAR_HARD_LIMIT  = 10500;
const CHAR_COLLAPSE    = 2000;

// Strip HTML tags to get plain text (for character counting)
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Styles
const styles = {
  container: {
    marginTop: '2rem'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#2d3748'
  },
  form: {
    background: 'white',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  formGroup: {
    marginBottom: '16px'
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  button: {
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  buttonSecondary: {
    background: '#e2e8f0',
    color: '#4a5568'
  },
  card: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    borderLeft: '4px solid #e2e8f0'
  },
  replyCard: {
    borderLeft: '4px solid #cbd5e0'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px'
  },
  userPhoto: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e2e8f0'
  },
  userPhotoPlaceholder: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '16px'
  },
  userName: {
    fontWeight: '600',
    color: '#2d3748'
  },
  messageContent: {
    fontSize: '15px',
    color: '#4a5568',
    lineHeight: '1.6',
    wordWrap: 'break-word',
    overflowWrap: 'break-word'
  },
  flagButton: {
    background: 'none',
    border: 'none',
    color: '#a0aec0',
    cursor: 'pointer',
    fontSize: '12px',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'color 0.2s, background 0.2s'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#718096',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px'
  },
  loginPrompt: {
    textAlign: 'center',
    padding: '20px',
    background: 'rgba(37, 99, 235, 0.1)',
    borderRadius: '8px',
    marginBottom: '20px'
  }
};

// Character Counter Component
const CharacterCounter = ({ length }) => {
  const color = length >= CHAR_SOFT_LIMIT ? '#e53e3e'
    : length >= CHAR_WARNING ? '#d69e2e'
    : '#48bb78';
  const overLimit = length > CHAR_HARD_LIMIT;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: '8px',
      marginTop: '6px',
      fontSize: '12px',
      color
    }}>
      {overLimit && (
        <span style={{ color: '#e53e3e', fontWeight: '600' }}>
          Please shorten your message to submit
        </span>
      )}
      <span style={{ fontWeight: '600' }}>
        {length.toLocaleString()} / {CHAR_SOFT_LIMIT.toLocaleString()}
      </span>
    </div>
  );
};

// Collapsible Message Content Component
const CollapsibleMessage = ({ content, style }) => {
  const [expanded, setExpanded] = useState(false);
  const plainText = stripHtml(content);
  const needsCollapse = plainText && plainText.length > CHAR_COLLAPSE;

  if (!needsCollapse) {
    return <div className="content-editor" style={style} dangerouslySetInnerHTML={{ __html: linkifyHtml(content) }} />;
  }

  return (
    <div style={style}>
      {expanded ? (
        <div className="content-editor" dangerouslySetInnerHTML={{ __html: linkifyHtml(content) }} />
      ) : (
        <div>{plainText.slice(0, CHAR_COLLAPSE) + '\u2026'}</div>
      )}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'block',
          marginTop: '8px',
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          padding: 0
        }}
      >
        {expanded ? '\u25B2 Show less' : '\u25BC Show more'}
      </button>
    </div>
  );
};

// User Photo Component
const UserPhoto = ({ userId, displayName, hasPhoto, size = 40 }) => {
  const photoStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '2px solid #e2e8f0'
  };
  
  const placeholderStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    background: '#2563eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: 'bold',
    fontSize: `${Math.max(size * 0.4, 10)}px`
  };

  if (hasPhoto && userId) {
    return (
      <img 
        src={`${USERS_API_URL}/users/${userId}/photo`}
        alt={displayName || 'User'}
        style={photoStyle}
        onError={(e) => {
          e.target.style.display = 'none';
          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
        }}
      />
    );
  }
  return (
    <div style={placeholderStyle}>
      {(displayName || 'U').charAt(0).toUpperCase()}
    </div>
  );
};

// Message Form Component
const MessageForm = ({ onSubmit, onCancel, replyTo, submitting }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState('');

  const textLength = stripHtml(content).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripHtml(content).trim()) {
      setError('Message content is required');
      return;
    }
    if (textLength > CHAR_HARD_LIMIT) {
      setError(`Message is too long (${textLength.toLocaleString()} characters). Please shorten it to around ${CHAR_SOFT_LIMIT.toLocaleString()} characters.`);
      return;
    }
    const success = await onSubmit({ 
      content, 
      parent_message_id: replyTo?.message_id || null
    });
    if (success) {
      setContent('');
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <h4 style={{ marginBottom: '16px' }}>
        {replyTo ? `Reply to ${replyTo.display_name || 'message'}` : 'Post a Message'}
      </h4>
      {error && <div style={{ color: '#fc8181', marginBottom: '12px' }}>{error}</div>}
      <div style={styles.formGroup}>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder="Share your thoughts about this amplifier..."
        />
        <CharacterCounter length={textLength} />
      </div>
      
      <div style={styles.buttonGroup}>
        <button type="submit" style={styles.button} disabled={submitting || textLength > CHAR_HARD_LIMIT}>
          {submitting ? 'Posting...' : (replyTo ? 'Post Reply' : 'Post Message')}
        </button>
        {onCancel && (
          <button 
            type="button" 
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

// Message List Component
const MessageList = ({ messages, onReply, currentUserId, replyTo, onSubmitReply, onCancelReply, submitting, compactMode = false }) => {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (messageId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  // Build a tree structure for threaded messages
  const buildMessageTree = (msgs) => {
    const messageMap = {};
    const roots = [];
    
    msgs.forEach(msg => {
      messageMap[msg.message_id] = { ...msg, replies: [] };
    });
    
    msgs.forEach(msg => {
      if (msg.parent_message_id && messageMap[msg.parent_message_id]) {
        messageMap[msg.parent_message_id].replies.push(messageMap[msg.message_id]);
      } else {
        roots.push(messageMap[msg.message_id]);
      }
    });
    
    return roots;
  };

  const renderMessage = (message, depth = 0) => {
    const isReply = depth > 0;
    const indentPx = depth * (compactMode ? 20 : 40);
    const isBeingRepliedTo = replyTo?.message_id === message.message_id;

    // ── compact mode ──
    if (compactMode && !expandedIds.has(message.message_id)) {
      const preview = stripHtml(message.message_content || '').replace(/\n/g, ' ');
      return (
        <div key={message.message_id}>
          <div
            onClick={() => toggleExpanded(message.message_id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              marginLeft: `${indentPx}px`,
              borderBottom: '1px solid #f0f0f0',
              background: 'white',
              cursor: 'pointer',
              minHeight: '32px',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
          >
            <UserPhoto userId={message.user_id} displayName={message.display_name} hasPhoto={message.has_photo} size={22} />
            <span style={{ fontWeight: '600', fontSize: '13px', color: '#2d3748', whiteSpace: 'nowrap' }}>
              {message.display_name || 'Anonymous'}
            </span>
            {message.has_image && <span title="Has image" style={{ fontSize: '12px', flexShrink: 0 }}>📷</span>}
            <span style={{
              fontSize: '13px',
              color: '#718096',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0
            }}>
              {preview}
            </span>
            <span style={{ fontSize: '11px', color: '#a0aec0', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {new Date(message.created_at).toLocaleDateString()}
            </span>
          </div>
          {message.replies && message.replies.length > 0 && (
            <div>{message.replies.map(r => renderMessage(r, depth + 1))}</div>
          )}
        </div>
      );
    }

    // ── full / expanded row ──
    const isInlineExpanded = compactMode && expandedIds.has(message.message_id);
    return (
      <div key={message.message_id}>
        {isInlineExpanded && (
          <div
            onClick={() => toggleExpanded(message.message_id)}
            style={{
              marginLeft: `${indentPx}px`,
              padding: '4px 12px',
              cursor: 'pointer',
              fontSize: '11px',
              color: '#2563eb',
              fontWeight: '600',
              userSelect: 'none'
            }}
          >
            ▲ Collapse
          </div>
        )}
        <div 
          style={{
            ...styles.card,
            ...(isReply ? styles.replyCard : {}),
            marginLeft: `${indentPx}px`,
            ...(isBeingRepliedTo ? { borderColor: '#4299e1', borderWidth: '2px' } : {})
          }}
        >
          <div style={styles.userInfo}>
            <UserPhoto 
              userId={message.user_id}
              displayName={message.display_name}
              hasPhoto={message.has_photo}
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={styles.userName}>{message.display_name || 'Anonymous'}</span>
                {message.is_industry_vip && <IndustryVIPBadge />}
                {message.is_verified_owner && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Verified Owner</span>
                )}
                {!message.is_verified_owner && parseInt(message.owned_count) >= 10 && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Amp Collector</span>
                )}
                {!message.is_verified_owner && parseInt(message.owned_count) >= 1 && parseInt(message.owned_count) < 10 && (
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '10px', padding: '1px 8px', whiteSpace: 'nowrap' }}>Member</span>
                )}
                {parseInt(message.wiki_trust_level) >= 1 && (
                  <WikiBadge trustLevel={parseInt(message.wiki_trust_level)} />
                )}
                {parseInt(message.discussion_level) >= 1 && (
                  <DiscussionBadge discussionLevel={parseInt(message.discussion_level)} />
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#a0aec0' }}>
                {new Date(message.created_at).toLocaleString()}
                {message.is_edited && ' (edited)'}
              </div>
            </div>
          </div>
          <CollapsibleMessage content={message.message_content} style={styles.messageContent} />
          {message.has_image && (
            <div style={{ marginTop: '12px' }}>
              <img 
                src={`${API_URL}/discussion-messages/${message.message_id}/image`}
                alt="Message attachment"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  objectFit: 'contain',
                  border: '1px solid #e2e8f0'
                }}
                onClick={(e) => window.open(e.target.src, '_blank')}
              />
            </div>
          )}
          {currentUserId && (
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button 
                style={styles.flagButton}
                onClick={() => onReply(message)}
              >
                ↩️ Reply
              </button>
            </div>
          )}
        </div>
        
        {isBeingRepliedTo && onSubmitReply && (
          <div style={{ marginLeft: `${(depth + 1) * 40}px`, marginTop: '8px', marginBottom: '8px' }}>
            <MessageForm 
              onSubmit={onSubmitReply}
              onCancel={onCancelReply}
              replyTo={replyTo}
              submitting={submitting}
            />
          </div>
        )}
        
        {message.replies && message.replies.length > 0 && (
          <div>
            {message.replies.map(reply => renderMessage(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const messageTree = buildMessageTree(messages);

  return (
    <div>
      {messageTree.map(msg => renderMessage(msg))}
    </div>
  );
};

// Main AmpDiscussion Component
const AmpDiscussion = ({ amplifierId, amplifierName }) => {
  const { user, isAuthenticated } = useAuth();
  const [topic, setTopic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [compactMode, setCompactMode] = useState(false);

  const fetchDiscussion = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/amplifiers/${amplifierId}/discussion`);
      setTopic(res.data.topic);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Error fetching discussion:', err);
    } finally {
      setLoading(false);
    }
  }, [amplifierId]);

  useEffect(() => {
    if (amplifierId) {
      fetchDiscussion();
    }
  }, [amplifierId, fetchDiscussion]);

  const handlePostMessage = async (messageData) => {
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('content', messageData.content);
      if (messageData.parent_message_id) {
        formData.append('parent_message_id', messageData.parent_message_id);
      }
      if (messageData.image) {
        formData.append('image', messageData.image);
      }

      await api.post(`/amplifiers/${amplifierId}/discussion`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReplyTo(null);
      await fetchDiscussion();
      return true;
    } catch (err) {
      console.error('Error posting message:', err);
      if (err.response?.data?.upgrade_required) {
        setUpgradeMessage(err.response.data.error);
        setShowUpgradeModal(true);
      } else if (err.response?.data?.code === 'TEXT_MODERATION_BLOCKED') {
        alert(err.response.data.error);
      } else {
        alert('Error posting message. Please try again.');
      }
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>Discussion</h2>
        </div>
        <div style={styles.emptyState}>Loading discussion...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UploadOverlay
        visible={submitting}
        message="Posting message…"
        subtext="Uploading & scanning for content safety"
      />
      <div style={styles.header}>
        <h2 style={styles.title}>Discussion</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {topic && (
            <span style={{ fontSize: '14px', color: '#718096' }}>
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
          )}
          {messages.length > 0 && (
            <button
              onClick={() => setCompactMode(!compactMode)}
              title={compactMode ? 'Expanded view' : 'Compact view'}
              style={{
                background: compactMode ? '#2563eb' : '#e2e8f0',
                color: compactMode ? 'white' : '#4a5568',
                border: 'none',
                borderRadius: '6px',
                padding: '5px 10px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'background 0.2s, color 0.2s'
              }}
            >
              ☰ Compact
            </button>
          )}
        </div>
      </div>

      {/* Login prompt for unauthenticated users */}
      {!isAuthenticated && (
        <div style={styles.loginPrompt}>
          <p style={{ margin: 0, color: '#4a5568' }}>
            <strong>Log in</strong> to join the discussion and share your thoughts about this amplifier.
          </p>
        </div>
      )}

      {/* Message form for authenticated users (when not replying inline) */}
      {isAuthenticated && !replyTo && (
        <MessageForm 
          onSubmit={handlePostMessage}
          submitting={submitting}
        />
      )}

      {/* Messages list or empty state */}
      {messages.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>No Discussion Yet</h3>
          <p>
            {isAuthenticated 
              ? 'Be the first to share your thoughts about this amplifier!' 
              : 'No messages yet. Log in to start the discussion.'}
          </p>
        </div>
      ) : (
        <MessageList 
          messages={messages}
          onReply={handleReply}
          currentUserId={user?.user_id}
          replyTo={replyTo}
          onSubmitReply={handlePostMessage}
          onCancelReply={handleCancelReply}
          submitting={submitting}
          compactMode={compactMode}
        />
      )}

      {/* Upgrade to Pro Modal */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
      />
    </div>
  );
};

export default AmpDiscussion;
